import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Monitor, LogOut, ShieldCheck, Copy, Check, User, Settings as SettingsIcon, Share2, FileSignature, ChevronRight, HelpCircle, MessageSquare, Receipt, Gift, Mail } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { usePageMeta, initials } from "@/lib/format";
import { useI18n } from "@/i18n";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Skeleton } from "@/components/ui/skeleton";
import { BillingForm } from "@/components/app/BillingForm";
import { ReferralQR } from "@/components/ReferralQR";
import { toast } from "@/components/ui/sonner";

export default function Account() {
  const { t, locale } = useI18n();
  usePageMeta(t("account.meta_title"));
  const { user, logout, logoutAll } = useAuth();
  const navigate = useNavigate();
  const [sessions, setSessions] = useState(null);
  const [copied, setCopied] = useState(false);
  const [affLink, setAffLink] = useState("");

  const fmtDate = (s) => {
    if (!s) return "—";
    try { return new Date(s).toLocaleString(locale, { dateStyle: "medium", timeStyle: "short" }); }
    catch { return s; }
  };

  useEffect(() => {
    api.get("/auth/sessions").then((r) => setSessions(r.data)).catch(() => setSessions({ sessions: [], count: 0 }));
    api.get("/me/referral/share-links").then((r) => setAffLink(r.data?.web_url || "")).catch(() => {});
  }, []);

  const onLogout = async () => {
    await logout();
    navigate("/");
  };
  const onLogoutAll = async () => {
    await logoutAll();
    toast.success(t("account.logout_all_done"));
    navigate("/login");
  };
  const copyReferral = () => {
    if (!affLink) return;
    try { navigator.clipboard.writeText(affLink); } catch { /* noop */ }
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };
  const shareReferral = async () => {
    if (!affLink) return;
    if (navigator.share) { try { await navigator.share({ title: "My Within", url: affLink }); return; } catch { /* cancelled */ } }
    copyReferral();
  };

  const billingDone = user?.billing_completed;

  return (
    <div className="max-w-3xl space-y-8">
      <h1 className="font-display text-3xl text-foreground">{t("account.title")}</h1>

      {/* Profile */}
      <section className="rounded-2xl border border-border bg-card p-6">
        <div className="flex items-center gap-4">
          <Avatar className="h-16 w-16">
            <AvatarImage src={user?.profile?.photo} alt={user?.name} />
            <AvatarFallback className="bg-primary text-lg text-primary-foreground">{initials(user?.name)}</AvatarFallback>
          </Avatar>
          <div className="min-w-0">
            <p className="font-display text-xl text-foreground">{user?.name}</p>
            <p className="truncate text-sm text-muted-foreground">{user?.email}</p>
            <Badge variant="secondary" className="mt-1 rounded-full capitalize">{user?.role}</Badge>
          </div>
        </div>
        {affLink ? (
          <div className="mt-5 space-y-4 rounded-xl border border-border bg-secondary/40 px-4 py-4">
            <div className="flex items-center justify-between gap-3">
              <div className="min-w-0">
                <p className="text-xs text-muted-foreground">{t("account.referral")}</p>
                <p className="truncate font-mono text-sm font-medium text-foreground" data-testid="account-affiliate-link">{affLink}</p>
              </div>
              <div className="flex shrink-0 items-center gap-1">
                <Button variant="ghost" size="sm" className="rounded-full" onClick={copyReferral} data-testid="copy-referral">
                  {copied ? <Check className="h-4 w-4 text-success" /> : <Copy className="h-4 w-4" />}
                </Button>
                <Button variant="ghost" size="sm" className="rounded-full" onClick={shareReferral} data-testid="share-referral">
                  <Share2 className="h-4 w-4" />
                </Button>
              </div>
            </div>
            <div className="flex justify-center">
              <ReferralQR value={affLink} testId="account-referral-qr" />
            </div>
          </div>
        ) : null}
      </section>

      {/* Konto-Hub navigation */}
      <section className="space-y-2">
        <h2 className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground/70">{t("account_more")}</h2>
        {[
          { icon: User, label: t("edit_profile"), to: "/app/account/profile", testId: "hub-profile" },
          { icon: SettingsIcon, label: t("settings"), to: "/app/account/settings", testId: "hub-settings" },
          { icon: Share2, label: t("data_sharing"), to: "/app/account/data-sharing", testId: "hub-data-sharing" },
          { icon: FileSignature, label: t("consent_forms_title"), to: "/app/account/consent", testId: "hub-consent" },
          { icon: Mail, label: t("weekly_digest"), to: "/app/account/weekly-digest", testId: "hub-weekly-digest" },
          { icon: Receipt, label: t("transaction_history"), to: "/app/account/purchases", testId: "hub-purchases" },
          { icon: Gift, label: t("account_redeem"), to: "/app/account/redeem", testId: "hub-redeem" },
          { icon: HelpCircle, label: t("account_help"), to: "/app/account/help", testId: "hub-help" },
          { icon: MessageSquare, label: t("support"), to: "/app/account/support", testId: "hub-support" },
        ].map((it) => (
          <button key={it.to} onClick={() => navigate(it.to)} data-testid={it.testId}
            className="flex w-full items-center gap-3 rounded-2xl border border-border bg-card p-3.5 text-left transition-colors hover:bg-accent">
            <it.icon className="h-5 w-5 text-primary" />
            <span className="flex-1 text-sm font-semibold text-foreground">{it.label}</span>
            <ChevronRight className="h-4 w-4 text-muted-foreground" />
          </button>
        ))}
      </section>

      {/* Billing */}
      <section className="rounded-2xl border border-border bg-card p-6">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="font-display text-xl text-foreground">{t("account.billing")}</h2>
          {billingDone ? (
            <Badge className="rounded-full"><ShieldCheck className="mr-1 h-3.5 w-3.5" /> {t("account.billing_complete")}</Badge>
          ) : (
            <Badge variant="secondary" className="rounded-full">{t("account.billing_incomplete")}</Badge>
          )}
        </div>
        <BillingForm />
      </section>

      {/* Sessions */}
      <section className="rounded-2xl border border-border bg-card p-6">
        <h2 className="mb-4 font-display text-xl text-foreground">{t("account.devices")}</h2>
        {sessions === null ? (
          <Skeleton className="h-20 w-full rounded-xl" />
        ) : sessions.sessions?.length ? (
          <div className="space-y-2">
            {sessions.sessions.map((s, i) => (
              <div key={s.family_id || i} className="flex items-center gap-3 rounded-xl border border-border px-4 py-3">
                <Monitor className="h-4 w-4 text-sage-light" />
                <div className="flex-1 text-sm">
                  <p className="text-foreground">{t("account.session", { n: i + 1 })}</p>
                  <p className="text-xs text-muted-foreground">
                    {t("account.logged_in")}: {fmtDate(s.issued_at)} · {t("account.last_active")}: {fmtDate(s.last_used_at)}
                  </p>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-sm text-muted-foreground">{t("account.no_sessions")}</p>
        )}

        <Separator className="my-5" />
        <div className="flex flex-wrap gap-3">
          <Button variant="outline" className="rounded-full" onClick={onLogout} data-testid="logout-btn">
            <LogOut className="mr-2 h-4 w-4" /> {t("account.logout")}
          </Button>
          <Button variant="destructive" className="rounded-full" onClick={onLogoutAll} data-testid="logout-all-btn">
            {t("account.logout_all")}
          </Button>
        </div>
      </section>
    </div>
  );
}
